import {ReactNode, useEffect, useRef, useState} from "react";
import {Link as RouterLink, useLocation} from "react-router-dom";
import Box from "@mui/material/Box";
import Collapse from "@mui/material/Collapse";
import ListItemButton from "@mui/material/ListItemButton";
import Link from "@mui/material/Link";
import Badge from "@mui/material/Badge";
import {MenuInterface} from "@/api/Settings/MenuSettings/MenuInterfaces";
import Hint from "@/components/Hint/Hint";

interface LeftMenuItemProps {
    to: string,
    title: string,
    alias: string,
    isNewWindow: boolean,
    isManager?: boolean,
    badgeCount?: number,
    children?: Omit<MenuInterface, "active">[]
}

interface MenuItemContentProps {
    title: string,
    hintAlias?: typeof hintAliases[number],
}

interface SimpleMenuItemProps {
    to: string,
    isNewWindow: boolean,
    badgeCount: number,
    content: ReactNode,
}

interface MenuItemWithChildrenProps {
    alias: string,
    badgeCount: number,
    content: ReactNode,
    children: Omit<MenuInterface, "active">[],
}

const hintAliases = [
    {
        alias: "create-new-order-from-partner",
        hintAlias: "create-new-order",
        left: "85%",
        bottom: "70%",
    },
    {
        alias: "knowledge-base",
        hintAlias: "knowledge-base",
        left: "90%",
        bottom: "60%",
    },
    {
        alias: "create-new-order",
        hintAlias: "create-new-order",
        left: "90%",
        bottom: "60%",
    },

]

const MenuItemContent = ({title, hintAlias}: MenuItemContentProps) => {
    if (!hintAlias) {
        return <>{title}</>;
    }

    return (
      <Hint alias={hintAlias.hintAlias} left={hintAlias.left} bottom={hintAlias.bottom}>
          {title}
      </Hint>
    );
};

const SimpleMenuItem = ({to, isNewWindow, badgeCount, content}: SimpleMenuItemProps) => (
  <div style={{position: "relative"}}>
      <Link
        component={RouterLink}
        underline="none"
        to={to}
        color="primary.dark"
        target={isNewWindow ? "_blank" : "_self"}
        sx={{display: "block"}}
      >
          <Badge
            badgeContent={badgeCount || undefined}
            color="error"
            sx={{display: "block", "& .MuiBadge-badge": {top: "10%", right: "3%"}}}
          >
              <ListItemButton sx={{width: "100%"}}>
                  {content}
              </ListItemButton>
          </Badge>
      </Link>
  </div>
);

const MenuItemWithChildren = ({alias, badgeCount, content, children}: MenuItemWithChildrenProps) => {
    const location = useLocation();
    const hasActiveChild = children.some((childItem) => childItem.url === location.pathname);
    const [expanded, setExpanded] = useState(hasActiveChild);
    const submenuRef = useRef<HTMLDivElement>(null);
    const shouldScrollOnExpand = useRef(false);

    useEffect(() => {
        if (hasActiveChild) {
            setExpanded(true);
        }
    }, [hasActiveChild]);

    const handleToggle = () => {
        const nextExpanded = !expanded;

        shouldScrollOnExpand.current = nextExpanded;
        setExpanded(nextExpanded);
    };

    const handleSubmenuEntered = () => {
        if (!shouldScrollOnExpand.current) {
            return;
        }

        shouldScrollOnExpand.current = false;
        submenuRef.current?.scrollIntoView({behavior: "smooth", block: "nearest"});
    };

    return (
      <>
          <Badge
            badgeContent={badgeCount || undefined}
            color="error"
            sx={{display: "block", "& .MuiBadge-badge": {top: "10%", right: "3%"}}}
          >
              <ListItemButton
                onClick={handleToggle}
                selected={hasActiveChild}
                aria-expanded={expanded}
                aria-controls={`${alias}-submenu`}
                sx={{
                    width: "100%",
                    color: "primary.dark",
                    "&.Mui-selected": {color: "primary.dark"},
                    "&.Mui-selected:hover": {color: "primary.dark"},
                }}
              >
                  {content}
              </ListItemButton>
          </Badge>

          <Collapse in={expanded} timeout="auto" onEntered={handleSubmenuEntered}>
              <Box ref={submenuRef} id={`${alias}-submenu`} role="group">
                  {children.map((childItem) => {
                    const isChildActive = childItem.url === location.pathname;

                    return (
                      <Link
                        key={childItem.id}
                        component={RouterLink}
                        underline="none"
                        to={childItem.url}
                        color="primary.dark"
                        target={childItem.is_new_window === 1 ? "_blank" : "_self"}
                      >
                          <ListItemButton
                            selected={isChildActive}
                            sx={{
                                pl: 5,
                                color: "primary.dark",
                                "&.Mui-selected": {color: "primary.dark"},
                                "&.Mui-selected:hover": {color: "primary.dark"},
                            }}
                          >
                              {childItem.title}
                          </ListItemButton>
                      </Link>
                    );
                  })}
              </Box>
          </Collapse>
      </>
    );
};

function LeftMenuItem({to, title, alias, isNewWindow, isManager = false, badgeCount = 0, children}: LeftMenuItemProps) {
    const hintAlias = hintAliases.find((item) => item.alias === alias);
    const menuTitle = isManager && title === "Оформить новый заказ"
      ? "Оформить новый заказ от имени партнёра"
      : title;
    const content = <MenuItemContent title={menuTitle} hintAlias={hintAlias}/>;

    if (children?.length) {
        return (
          <MenuItemWithChildren
            alias={alias}
            badgeCount={badgeCount}
            content={content}
            children={children}
          />
        );
    }

    return (
      <SimpleMenuItem
        to={to}
        isNewWindow={isNewWindow}
        badgeCount={badgeCount}
        content={content}
      />
    );
}

export default LeftMenuItem;
